// @section 1 Card Capture - Atlas /card-capture (prospect-conveyor front end).
//
// Mobile-first: used standing in a parking lot after a BD event. Flow:
//   1. Pick (or create) the event as the active capture context.
//   2. Photograph one or more business cards (camera file input).
//   3. Each photo is OCR'd on-device (Tesseract.js, no billing), fields pre-fill
//      the editable review card, and the image + raw text upload to Drive.
//   4. Record a voice note per handshake (CardVoiceNote) - audio archived to
//      Drive, live transcript paired to the card capture.
//   5. Confirm -> upserts hq_crm company + contact (first_met_event_id) and
//      optionally enqueues the prospect research checklist run.
// Unconfirmed captures from earlier sessions surface at the top so nothing
// falls through the cracks.

import { useCallback, useEffect, useRef, useState } from 'react';
import CardVoiceNote, { VoiceNoteResult } from '../components/CardVoiceNote';
import { recognizeCard, disposeCardOcr, warmUpCardOcr } from '../lib/cardOcr';
import { parseCardText } from '../lib/parseCardText';
import {
  CardFields,
  HqCapture,
  HqEvent,
  confirmCapture,
  createEvent,
  listCaptures,
  listEvents,
  parseCapture,
  uploadCardCapture,
  uploadVoiceCapture,
} from '../api';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
  border: '1px solid var(--line)', background: 'var(--bg-1, rgba(255,255,255,0.03))',
  color: 'var(--tx-1)', fontSize: 14,
};
const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--tx-3)', display: 'block' };

// @section 2 Page component
export default function CardCapture() {
  const [events, setEvents] = useState<HqEvent[] | null>(null);
  const [activeEvent, setActiveEvent] = useState<HqEvent | null>(null);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [pending, setPending] = useState<HqCapture[]>([]);
  const [cards, setCards] = useState<CardState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const nextLocalId = useRef(1);

  useEffect(() => {
    void (async () => {
      try {
        const evs = await listEvents();
        setEvents(evs);
        // Default to today's event when there is one.
        const today = new Date().toISOString().slice(0, 10);
        setActiveEvent(evs.find((e) => e.eventDate?.slice(0, 10) === today) ?? null);
        const caps = await listCaptures({ kind: 'card_image' });
        setPending(caps.filter((c) => c.status !== 'completed'));
      } catch (e) { setError(String(e)); }
    })();
  }, []);

  // @block 2.1 A photo was taken/picked - OCR on-device, then upload to Drive.
  const onFiles = useCallback((files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      const localId = nextLocalId.current++;
      const previewUrl = URL.createObjectURL(file);
      setCards((prev) => [{ localId, previewUrl, phase: 'uploading', fields: {}, checklist: true }, ...prev]);
      void (async () => {
        try {
          // Primary OCR runs here on the device (no billing). Best-effort: empty
          // text just means the operator types the fields (or, Phase 2, taps the
          // cloud-OCR backup). The image + raw text still upload for the record.
          const { text } = await recognizeCard(file);
          const fields = text ? parseCardText(text) : {};
          const r = await uploadCardCapture(file, {
            ...(activeEvent ? { eventId: activeEvent.id } : {}),
            ...(text ? { ocrText: text } : {}),
          });
          setCards((prev) => prev.map((c) => c.localId === localId
            ? {
                ...c, phase: 'review', captureId: r.captureId,
                fields, ocrText: text || undefined,
                uploadError: text ? undefined : 'No text read on device - enter the fields by hand.',
              }
            : c));
        } catch (e) {
          setCards((prev) => prev.map((c) => c.localId === localId
            ? { ...c, phase: 'failed', uploadError: String(e) }
            : c));
        }
      })();
    }
    if (fileRef.current) fileRef.current.value = '';
  }, [activeEvent]);

  // @block 2.2 Warm the OCR engine on open so the first scan is instant in front
  // of a prospect; free the worker + its WASM heap when leaving the surface.
  useEffect(() => { warmUpCardOcr(); return () => { void disposeCardOcr(); }; }, []);

  // @block 2.3 Resume an unconfirmed capture from a previous session.
  const resumeCapture = useCallback((cap: HqCapture) => {
    const localId = nextLocalId.current++;
    setPending((prev) => prev.filter((p) => p.id !== cap.id));
    // Re-parse the stored on-device OCR text so fields pre-fill without another
    // scan. If none was stored (image-only capture), offer the re-extract action.
    const fields = cap.ocrText ? parseCardText(cap.ocrText) : {};
    setCards((prev) => [
      { localId, phase: 'review', captureId: cap.id, fields, ocrText: cap.ocrText ?? undefined, checklist: true, needsParse: !cap.ocrText },
      ...prev,
    ]);
  }, []);

  if (error) return <div className="v5-page" style={{ padding: 24, color: 'var(--neg)' }}>API error: {error}</div>;

  return (
    <div style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="v5-page" style={{ padding: '20px 16px 48px', maxWidth: 640, margin: '0 auto' }}>
        <div className="page-head" style={{ padding: 0 }}>
          <div className="crumb"><span>Atlas</span><span className="sep">›</span><b>Card Capture</b></div>
          <div className="page-title">
            <h1>Card Capture</h1>
            <div className="sub">Scan cards, narrate the handshake, queue the research.</div>
          </div>
        </div>

        {/* @block 2.3 Event context */}
        <div style={{ marginTop: 16 }}>
          <span style={labelStyle}>Event</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {(events ?? []).slice(0, 6).map((e) => (
              <button key={e.id} type="button" onClick={() => setActiveEvent(activeEvent?.id === e.id ? null : e)}
                style={{
                  fontSize: 13, padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                  border: activeEvent?.id === e.id ? '1px solid var(--brand-3)' : '1px solid var(--line)',
                  background: activeEvent?.id === e.id ? 'var(--brand-3)' : 'transparent',
                  color: activeEvent?.id === e.id ? 'var(--bg-0, #0b0b0c)' : 'var(--tx-2)',
                }}>
                {e.name}{e.eventDate ? ` · ${e.eventDate.slice(5, 10)}` : ''}
              </button>
            ))}
            <button type="button" onClick={() => setShowNewEvent((v) => !v)}
              style={{ fontSize: 13, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', border: '1px dashed var(--line)', background: 'transparent', color: 'var(--tx-3)' }}>
              + New event
            </button>
          </div>
          {events === null && <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 6 }}>Loading events…</div>}
          {showNewEvent && (
            <NewEventForm onCreated={(e) => { setEvents((prev) => [e, ...(prev ?? [])]); setActiveEvent(e); setShowNewEvent(false); }} />
          )}
        </div>

        {/* @block 2.4 Camera trigger */}
        <input
          ref={fileRef} type="file" accept="image/*" capture="environment" multiple
          style={{ display: 'none' }} onChange={(e) => onFiles(e.target.files)}
        />
        <button type="button" onClick={() => fileRef.current?.click()}
          style={{
            width: '100%', marginTop: 16, padding: '18px 16px', borderRadius: 12,
            fontSize: 16, fontWeight: 700, cursor: 'pointer', border: 'none',
            background: 'var(--brand-3)', color: 'var(--bg-0, #0b0b0c)',
          }}>
          ▣ Photograph business card
        </button>
        {!activeEvent && (
          <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 6 }}>
            No event selected - captures will save without an event context.
          </div>
        )}

        {/* @block 2.5 Unconfirmed captures from earlier sessions */}
        {pending.length > 0 && (
          <div style={{ marginTop: 20, border: '1px solid var(--warn)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warn)' }}>
              {pending.length} unconfirmed card{pending.length > 1 ? 's' : ''} from earlier
            </div>
            {pending.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--tx-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.eventName ?? 'no event'} · {new Date(p.capturedAt).toLocaleString()}
                  {p.ocrText ? ` · ${p.ocrText.split('\n')[0]}` : ''}
                </span>
                <button type="button" onClick={() => resumeCapture(p)}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--line)', background: 'transparent', color: 'var(--tx-2)', cursor: 'pointer' }}>
                  Review
                </button>
              </div>
            ))}
          </div>
        )}

        {/* @block 2.6 Capture cards */}
        <div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
          {cards.map((card) => (
            <CardReview key={card.localId} card={card} eventId={activeEvent?.id}
              onChange={(patch) => setCards((prev) => prev.map((c) => c.localId === card.localId ? { ...c, ...patch } : c))}
              onRemove={() => setCards((prev) => prev.filter((c) => c.localId !== card.localId))}
            />
          ))}
        </div>

        {cards.length === 0 && pending.length === 0 && (
          <div style={{ color: 'var(--tx-4)', fontStyle: 'italic', padding: '24px 0', textAlign: 'center' }}>
            Pick the event, then photograph the first card.
          </div>
        )}
      </div>
    </div>
  );
}

// @section 3 New event form
function NewEventForm({ onCreated }: { onCreated: (e: HqEvent) => void }) {
  const [name, setName] = useState('');
  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 10));
  const [organizer, setOrganizer] = useState('');
  const [venue, setVenue] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) { setErr('Event name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const e = await createEvent({
        name: name.trim(), eventDate: eventDate || undefined,
        organizer: organizer.trim() || undefined, venue: venue.trim() || undefined,
      });
      onCreated(e);
    } catch (e) { setErr(String(e)); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 14, marginTop: 10, display: 'grid', gap: 10 }}>
      <label style={labelStyle}>Event name *
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mesa Chamber Mixer" />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={labelStyle}>Date
          <input style={inputStyle} type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
        </label>
        <label style={labelStyle}>Organizer
          <input style={inputStyle} value={organizer} onChange={(e) => setOrganizer(e.target.value)} placeholder="Mesa Chamber / BNI…" />
        </label>
      </div>
      <label style={labelStyle}>Venue
        <input style={inputStyle} value={venue} onChange={(e) => setVenue(e.target.value)} />
      </label>
      {err && <div style={{ color: 'var(--neg)', fontSize: 12 }}>{err}</div>}
      <button type="button" onClick={save} disabled={saving}
        style={{ padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--brand-3)', color: 'var(--bg-0, #0b0b0c)', opacity: saving ? 0.6 : 1, justifySelf: 'start' }}>
        {saving ? 'Saving…' : 'Create event'}
      </button>
    </div>
  );
}

// @section 4 Per-card review
type CardState = {
  localId: number;
  phase: 'uploading' | 'review' | 'confirmed' | 'failed';
  captureId?: string;
  previewUrl?: string;
  fields: CardFields;
  ocrText?: string;
  uploadError?: string;
  checklist: boolean;
  needsParse?: boolean;
};

function CardReview({
  card, eventId, onChange, onRemove,
}: {
  card: CardState;
  eventId?: string;
  onChange: (patch: Partial<CardState>) => void;
  onRemove: () => void;
}) {
  const [voiceState, setVoiceState] = useState<'none' | 'uploading' | 'done' | 'failed'>('none');
  const [transcript, setTranscript] = useState('');
  const [notes, setNotes] = useState('');
  const [meetingAt, setMeetingAt] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ checklistRunId?: string | null; checklistNote?: string | null } | null>(null);

  const f = card.fields;
  const setField = (k: keyof CardFields, v: string) => onChange({ fields: { ...f, [k]: v } });

  // @block 4.1 Voice note recorded -> upload paired to this card.
  const onNote = useCallback((note: VoiceNoteResult) => {
    setTranscript(note.transcript);
    if (!card.captureId) return;
    setVoiceState('uploading');
    void uploadVoiceCapture({
      blob: note.blob ?? undefined,
      transcript: note.transcript || undefined,
      durationMs: note.durationMs,
      eventId,
      pairedCaptureId: card.captureId,
    })
      .then(() => setVoiceState('done'))
      .catch(() => setVoiceState('failed'));
  }, [card.captureId, eventId]);

  // @block 4.2 Re-extract fields. Prefer text already stored on the capture
  // (parsed client-side, no round-trip); if none, ask the server, which returns
  // the stored OCR text today and the Azure Vision Read result once Phase 2 lands.
  const reparse = async () => {
    if (!card.captureId) return;
    setParsing(true); setErr(null);
    try {
      let text = card.ocrText;
      if (!text) {
        const r = await parseCapture(card.captureId);
        text = r.ocrText ?? undefined;
      }
      onChange({ fields: text ? parseCardText(text) : {}, ocrText: text, needsParse: false });
    } catch (e) { setErr(String(e)); }
    finally { setParsing(false); }
  };

  // @block 4.3 Confirm -> upsert CRM + optional checklist run.
  const confirm = async () => {
    if (!card.captureId) return;
    const companyName = (f.companyName ?? '').trim();
    if (!companyName && !(f.firstName ?? '').trim() && !(f.email ?? '').trim()) {
      setErr('Need at least a company name or a contact name/email.');
      return;
    }
    setConfirming(true); setErr(null);
    try {
      const r = await confirmCapture(card.captureId, {
        company: { name: companyName, domain: f.website ?? undefined },
        contact: {
          firstName: f.firstName ?? undefined, lastName: f.lastName ?? undefined,
          email: f.email ?? undefined, phone: f.phone ?? undefined, title: f.title ?? undefined,
        },
        enqueueChecklist: card.checklist,
        meetingAt: meetingAt ? new Date(meetingAt).toISOString() : undefined,
        notes: [f.notes, notes].filter(Boolean).join('\n') || undefined,
      });
      setResult({ checklistRunId: r.checklistRunId, checklistNote: r.checklistNote });
      onChange({ phase: 'confirmed' });
    } catch (e) { setErr(String(e)); }
    finally { setConfirming(false); }
  };

  const border = card.phase === 'confirmed' ? 'var(--pos)' : card.phase === 'failed' ? 'var(--neg)' : 'var(--line)';

  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 12, padding: 16, background: 'var(--bg-1, rgba(255,255,255,0.02))' }}>
      {/* @block 4.4 Header: thumbnail + phase */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {card.previewUrl && (
          <img src={card.previewUrl} alt="card" style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {card.phase === 'uploading' && <div style={{ fontSize: 13, color: 'var(--tx-3)' }}>Uploading + reading card…</div>}
          {card.phase === 'failed' && <div style={{ fontSize: 13, color: 'var(--neg)' }}>Upload failed: {card.uploadError}</div>}
          {card.phase === 'confirmed' && (
            <div style={{ fontSize: 13, color: 'var(--pos)', fontWeight: 600 }}>
              ✓ Saved to CRM
              {result?.checklistRunId && ' · research checklist queued'}
              {result?.checklistNote === 'no_active_prospect_template' && ' · checklist NOT queued (no active prospect template yet)'}
            </div>
          )}
          {card.phase === 'review' && (
            <div style={{ fontSize: 13, color: 'var(--tx-2)', fontWeight: 600 }}>
              {(f.firstName || f.lastName) ? `${f.firstName ?? ''} ${f.lastName ?? ''}`.trim() : 'Review card'}
              {f.companyName ? ` - ${f.companyName}` : ''}
            </div>
          )}
          {card.uploadError && card.phase === 'review' && (
            <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 2 }}>{card.uploadError}</div>
          )}
        </div>
        {card.phase !== 'confirmed' && (
          <button type="button" onClick={onRemove} title="Dismiss"
            style={{ border: 'none', background: 'transparent', color: 'var(--tx-4)', cursor: 'pointer', fontSize: 16 }}>×</button>
        )}
      </div>

      {/* @block 4.5 Review form */}
      {card.phase === 'review' && (
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          {card.needsParse && (
            <button type="button" onClick={reparse} disabled={parsing}
              style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--brand-3)', background: 'transparent', color: 'var(--brand-3)', cursor: 'pointer', justifySelf: 'start' }}>
              {parsing ? 'Extracting…' : '↻ Extract fields from OCR'}
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>First name
              <input style={inputStyle} value={f.firstName ?? ''} onChange={(e) => setField('firstName', e.target.value)} />
            </label>
            <label style={labelStyle}>Last name
              <input style={inputStyle} value={f.lastName ?? ''} onChange={(e) => setField('lastName', e.target.value)} />
            </label>
            <label style={{ ...labelStyle, gridColumn: '1 / span 2' }}>Company
              <input style={inputStyle} value={f.companyName ?? ''} onChange={(e) => setField('companyName', e.target.value)} />
            </label>
            <label style={labelStyle}>Title
              <input style={inputStyle} value={f.title ?? ''} onChange={(e) => setField('title', e.target.value)} />
            </label>
            <label style={labelStyle}>Phone
              <input style={inputStyle} inputMode="tel" value={f.phone ?? ''} onChange={(e) => setField('phone', e.target.value)} />
            </label>
            <label style={labelStyle}>Email
              <input style={inputStyle} inputMode="email" value={f.email ?? ''} onChange={(e) => setField('email', e.target.value)} />
            </label>
            <label style={labelStyle}>Website
              <input style={inputStyle} inputMode="url" value={f.website ?? ''} onChange={(e) => setField('website', e.target.value)} />
            </label>
          </div>

          {/* @block 4.6 Voice note */}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <CardVoiceNote onNote={onNote} disabled={voiceState === 'uploading'} />
            {voiceState === 'uploading' && <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 4 }}>Saving voice note…</div>}
            {voiceState === 'done' && <div style={{ fontSize: 12, color: 'var(--pos)', marginTop: 4 }}>Voice note saved.</div>}
            {voiceState === 'failed' && <div style={{ fontSize: 12, color: 'var(--neg)', marginTop: 4 }}>Voice note upload failed - the transcript below still saves with Confirm.</div>}
            {transcript && (
              <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 6, fontStyle: 'italic' }}>“{transcript}”</div>
            )}
          </div>

          <label style={labelStyle}>Notes (who they were, what they said, next step)
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {/* @block 4.7 Checklist + meeting */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: 'var(--tx-2)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={card.checklist} onChange={(e) => onChange({ checklist: e.target.checked })} />
              Queue research checklist
            </label>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
              Meeting booked?
              <input style={{ ...inputStyle, width: 'auto', padding: '6px 8px' }} type="datetime-local" value={meetingAt} onChange={(e) => setMeetingAt(e.target.value)} />
            </label>
          </div>

          {err && <div style={{ color: 'var(--neg)', fontSize: 12 }}>{err}</div>}
          <button type="button" onClick={confirm} disabled={confirming || voiceState === 'uploading'}
            style={{ padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', background: 'var(--pos)', color: 'var(--bg-0, #0b0b0c)', opacity: confirming ? 0.6 : 1 }}>
            {confirming ? 'Saving…' : 'Confirm → CRM'}
          </button>
        </div>
      )}
    </div>
  );
}
